import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "../lib/api";
import Seo from "../components/Seo";
import { useCart } from "../context/CartContext";
import { fmtEur } from "./Boutique";
import { CheckCircle2, Loader2, Store } from "lucide-react";

const BoutiqueSuccess = () => {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { clear } = useCart();
  const [order, setOrder] = useState(null);
  const [status, setStatus] = useState("checking");
  const tries = useRef(0);

  useEffect(() => {
    if (!sessionId) { setStatus("error"); return; }
    let timer;
    const poll = async () => {
      tries.current += 1;
      try {
        const { data } = await api.get(`/shop/orders/status/${sessionId}`);
        setOrder(data);
        if (data.paymentStatus === "paid") { setStatus("paid"); clear(); return; }
      } catch { /* la commande peut ne pas être encore visible */ }
      if (tries.current >= 12) { setStatus("pending"); return; }
      timer = setTimeout(poll, 2500);
    };
    poll();
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return (
    <div className="max-w-xl mx-auto px-6 py-20 text-center" data-testid="shop-success-page">
      <Seo title="Commande confirmée" description="Votre commande boutique est confirmée." />
      {status === "checking" && (
        <>
          <Loader2 className="animate-spin mx-auto text-deep-rose mb-5" size={32} />
          <h1 className="font-serif-display text-2xl text-stone-800">Validation de votre paiement…</h1>
          <p className="text-stone-500 mt-2 text-sm">Merci de patienter quelques secondes.</p>
        </>
      )}
      {status === "paid" && (
        <>
          <CheckCircle2 className="mx-auto text-emerald-600 mb-5" size={44} />
          <h1 className="font-serif-display text-3xl text-stone-800" data-testid="shop-success-title">Merci pour votre commande&nbsp;!</h1>
          <p className="text-stone-600 mt-3">
            Commande <strong data-testid="shop-success-number">{order?.orderNumber}</strong> — {fmtEur(order?.total)} réglés par carte.
          </p>
          <div className="bg-blush-light/50 border border-blush rounded-2xl p-4 mt-6 text-left text-sm text-stone-700">
            <p className="flex items-start gap-2"><Store size={16} className="text-deep-rose shrink-0 mt-0.5" />
              Votre commande est à <strong>retirer à l&apos;école</strong>. Vous recevrez un email dès qu&apos;elle sera prête.</p>
          </div>
          <div className="flex gap-3 justify-center mt-8 flex-wrap">
            <Link to="/boutique" className="bg-coral text-white px-6 py-3 rounded-full text-sm tracking-widest uppercase">Retour à la boutique</Link>
            <Link to="/espace-famille/boutique" className="border border-stone-300 text-stone-700 px-6 py-3 rounded-full text-sm tracking-widest uppercase">Mes commandes</Link>
          </div>
        </>
      )}
      {status === "pending" && (
        <>
          <h1 className="font-serif-display text-2xl text-stone-800">Paiement en cours de validation</h1>
          <p className="text-stone-600 mt-3 text-sm">
            Votre banque n&apos;a pas encore confirmé le paiement. Vous recevrez l&apos;email de confirmation dès qu&apos;il sera validé.
          </p>
          <Link to="/boutique" className="inline-block mt-8 bg-coral text-white px-6 py-3 rounded-full text-sm tracking-widest uppercase">Retour à la boutique</Link>
        </>
      )}
      {status === "error" && (
        <>
          <h1 className="font-serif-display text-2xl text-stone-800">Session introuvable</h1>
          <Link to="/boutique" className="inline-block mt-8 bg-coral text-white px-6 py-3 rounded-full text-sm tracking-widest uppercase">Retour à la boutique</Link>
        </>
      )}
    </div>
  );
};

export default BoutiqueSuccess;
